import { SearchField, GroupedUsers, UserSelectorDialog } from '@halo/ui-chat-react';
import type { GroupMember } from '@halo/chat-sdk-react';
import { StateEnum } from '@halo/chat-sdk-react';
import { useSearch } from './helpers';

interface GroupInviteDialogProps {
  checkUserKey: boolean;
  members: GroupMember[];
  isOpen: boolean;
  exceptionProps?: {
    title: string;
    isException: boolean;
    exceptionList: GroupMember[];
  };
  setIsOpen: (isOpen: boolean) => void;
  onOK: (selected: any[]) => Promise<any>;
  onClose?: () => void;
}

const GroupAddAdminDialog = ({
  checkUserKey,
  members,
  isOpen,
  exceptionProps,
  setIsOpen,
  onOK,
}: GroupInviteDialogProps) => {
  const { t } = useTranslation();
  const [resetTrigger, setResetTrigger] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const { isException, exceptionList, title } = exceptionProps ?? {};

  const { list } = useSearch(searchValue, resetTrigger, isOpen, members);

  const listFormatted = useMemo(() => {
    return list.map(({ items }) => {
      const filteredItems = items?.map((item) => {
        const isOwner = item.role === 1;
        const isAdmin = item.role === 1 || item.role === 2;
        const isDelete = item.state === StateEnum.DELETE_ACCOUNT;
        const isReadyException = exceptionList?.some(
          (exception) => exception.user_id === item.user_id
        );

        if (isException) {
          return isReadyException
            ? { ...item, disabled: true, defaultChecked: true }
            : isDelete || isOwner
              ? { ...item, disabled: true }
              : item;
        } else {
          return isAdmin
            ? { ...item, disabled: true, defaultChecked: true }
            : isDelete
              ? { ...item, disabled: true }
              : item;
        }
      });
      return { items: filteredItems };
    });
  }, [list, isException, exceptionList]);

  const {
    listFlat,
    selected,
    canSelectList,
    isSelectedAll,
    toggleSelectUser,
    onSelectAllChange,
    onRemove,
    resetDialogState,
  } = UserSelectorDialog.useUserSelector(listFormatted, checkUserKey, 10);

  useLayoutEffect(() => {
    if (!isOpen) {
      resetDialogState();
      setSearchValue('');
      setResetTrigger((prev) => prev + 1);
    }
  }, [isOpen, resetDialogState]);

  return (
    <UserSelectorDialog.Root isOpen={isOpen} setIsOpen={setIsOpen}>
      <UserSelectorDialog.Header title={title || t('chat.addGroupAdmin')} />
      <UserSelectorDialog.Body
        selectedList={
          <UserSelectorDialog.SelectedList
            selected={selected}
            onRemove={onRemove}
            checkUserKey={checkUserKey}
          />
        }
      >
        <SearchField searchValue={searchValue} onChange={setSearchValue} />
        <GroupedUsers
          list={listFormatted}
          toggleSelectUser={toggleSelectUser}
          selected={selected}
          checkUserKey={checkUserKey}
        />
        {listFlat.length > 0 && canSelectList.length > 0 && (
          <UserSelectorDialog.SelectAll isChecked={isSelectedAll} onChange={onSelectAllChange} />
        )}
      </UserSelectorDialog.Body>
      <UserSelectorDialog.Footer
        selected={selected}
        onOK={onOK}
        cancelText={t('buttons.cancel')}
        okText={t('buttons.confirm')}
      />
    </UserSelectorDialog.Root>
  );
};

const MemoizedGroupAddAdminDialog = memo(GroupAddAdminDialog);

export { MemoizedGroupAddAdminDialog as GroupAddAdminDialog };
